import { motion } from 'framer-motion';

const STROKE_WIDTH = 4;
const RING_INSET = 3; // extra inset so stroke never touches SVG viewBox edge

const ringColor = (score) => {
  if (!score || score === 0) return '#d1d5db'; // gray-300
  if (score >= 80) return '#22c55e'; // green-500
  if (score >= 60) return '#f59e0b'; // amber-500
  return '#ef4444'; // red-500
};

const trackColor = (score, isDark) => {
  if (!score || score === 0) return isDark ? '#1e293b' : '#f3f4f6';
  if (score >= 80) return isDark ? '#14532d22' : '#dcfce7';
  if (score >= 60) return isDark ? '#78350f22' : '#fef3c7';
  return isDark ? '#7f1d1d22' : '#fee2e2';
};

/**
 * PlatformRing — Instagram Stories-style SVG progress ring
 *
 * Props:
 *   platform  { icon: Component, color: string, bgColor: string, brandColor: string, label: string, value: string }
 *   score     number 0–100
 *   size      number (px, default 72)
 *   showLabel boolean (default true)
 *   showScore boolean (default true)
 *   onClick   function
 *   isDark    boolean (pass dark mode state for track bg)
 *   delay     animation delay in seconds (default 0)
 */
const PlatformRing = ({
  platform,
  score = 0,
  connected: connectedProp,
  size = 72,
  showLabel = true,
  showScore = true,
  onClick,
  isDark = false,
  delay = 0,
}) => {
  const r = (size - STROKE_WIDTH * 2 - RING_INSET * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const progress = score > 0 ? score / 100 : 0;
  const dashOffset = circumference * (1 - progress);
  const Icon = platform?.icon;
  const iconSize = Math.round(size * 0.36);
  // Use explicit connected prop if provided, otherwise fall back to score > 0
  const connected = connectedProp !== undefined ? connectedProp : score > 0;
  const color = connected ? ringColor(score || 100) : ringColor(0);

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 flex-shrink-0 group focus:outline-none"
      style={{ minWidth: size }}
    >
      {/* Ring + icon */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="block"
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={trackColor(score, isDark)}
            strokeWidth={STROKE_WIDTH}
          />
          {/* Progress arc */}
          {score > 0 && (
            <motion.circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: dashOffset }}
              transition={{ duration: 0.9, ease: 'easeOut', delay }}
            />
          )}
        </svg>

        {/* Center icon */}
        <div
          className="absolute inset-0 flex items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-105"
          style={{
            top: STROKE_WIDTH + 2,
            left: STROKE_WIDTH + 2,
            right: STROKE_WIDTH + 2,
            bottom: STROKE_WIDTH + 2,
            background: connected
              ? (platform?.brandColor ? `${platform.brandColor}18` : '#f3f4f6')
              : (isDark ? '#1e293b' : '#f9fafb'),
          }}
        >
          {Icon ? (
            <Icon
              style={{
                width: iconSize,
                height: iconSize,
                fontSize: iconSize,
                color: connected ? (platform?.brandColor || color) : '#9ca3af',
              }}
            />
          ) : (
            <span
              className="font-bold"
              style={{ fontSize: iconSize * 0.7, color: connected ? color : '#9ca3af' }}
            >
              {platform?.label?.charAt(0) || '?'}
            </span>
          )}
        </div>
      </div>

      {/* Label */}
      {showLabel && (
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 text-center leading-tight">
          {platform?.label || ''}
        </span>
      )}

      {/* Score */}
      {showScore && (
        <span
          className="text-xs font-bold leading-none"
          style={{ color: connected ? color : '#9ca3af' }}
        >
          {connected ? (score || '✓') : '—'}
        </span>
      )}
    </button>
  );
};

export default PlatformRing;
